/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the publish dialog (SPEC_WORKFLOW §7, plan H4).

   A publish is complete only when every affected variant has answered,
   so this dialog walks them: one row per variant with Accept all / Keep
   mine / Review block by block (the Updates panel's own cards, inline,
   answering into dialog state), plus "Apply to all variants". Publish
   stays disabled until every pending change has an answer. Nothing is
   written until Publish — then ONE batch (useSpecMasterDraft.commitPublish).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import type { ClientLayer } from '../../shared/types';
import { pendingCount, type VariantChangeEntry } from '../../shared/variant-updates';
import { UpdatesPanel } from './UpdatesPanel';
import {
  affectedEntries,
  answerEntry,
  isPublishReady,
  type ChangeDecision,
  type PublishAnswers,
  type PublishPlan,
} from './publishPlan';

type RowChoice = 'accept' | 'keep' | 'review';

const CHOICES: { id: RowChoice; label: string }[] = [
  { id: 'accept', label: 'Accept all' },
  { id: 'keep', label: 'Keep mine' },
  { id: 'review', label: 'Review block by block' },
];

/** The entry as the dialog's answers would leave it — what the inline
 *  Updates cards render. */
function withAnswers(entry: VariantChangeEntry, answers: PublishAnswers): VariantChangeEntry {
  const mine = answers[entry.id] || {};
  return {
    ...entry,
    changes: (entry.changes || []).map((c) =>
      c.decision === 'pending' && mine[c.changeId] ? { ...c, decision: mine[c.changeId] } : c
    ),
  };
}

export function variantNameOf(layers: ClientLayer[], entry: VariantChangeEntry): string {
  return layers.find((l) => l.id === entry.layerId)?.name || 'Variant';
}

export function PublishDialog({
  plan,
  notice,
  busy,
  onCancel,
  onPublish,
}: {
  plan: PublishPlan;
  /** Inline notice (the draft moved while the dialog was open). */
  notice?: string | null;
  busy: boolean;
  onCancel: () => void;
  onPublish: (answers: PublishAnswers) => void;
}) {
  const affected = React.useMemo(() => affectedEntries(plan), [plan]);
  const [answers, setAnswers] = React.useState<PublishAnswers>({});
  const [choice, setChoice] = React.useState<Record<string, RowChoice>>({});
  const ready = isPublishReady(plan, answers);

  const setRow = (entry: VariantChangeEntry, next: RowChoice) => {
    setChoice((c) => ({ ...c, [entry.id]: next }));
    if (next === 'review') return; // answers come from the cards
    const decision: ChangeDecision = next === 'accept' ? 'accepted' : 'rejected';
    setAnswers((a) => ({ ...a, [entry.id]: answerEntry(entry, decision) }));
  };
  const applyToAll = (decision: ChangeDecision) => {
    const nextChoice: Record<string, RowChoice> = {};
    const nextAnswers: PublishAnswers = {};
    for (const e of affected) {
      nextChoice[e.id] = decision === 'accepted' ? 'accept' : 'keep';
      nextAnswers[e.id] = answerEntry(e, decision);
    }
    setChoice(nextChoice);
    setAnswers(nextAnswers);
  };
  const decideOne = (entryId: string, changeId: string, decision: ChangeDecision) =>
    setAnswers((a) => ({ ...a, [entryId]: { ...(a[entryId] || {}), [changeId]: decision } }));
  const decideAllOf = (entry: VariantChangeEntry, decision: ChangeDecision) =>
    setAnswers((a) => ({
      ...a,
      [entry.id]: { ...answerEntry(entry, decision), ...(a[entry.id] || {}) },
    }));

  const names = affected.map((e) => variantNameOf(plan.layers, e)).join(', ');
  const blocksLine = `${plan.changedBlocks} block${plan.changedBlocks === 1 ? '' : 's'} changed`;
  const allChoice = affected.length
    ? affected.every((e) => choice[e.id] === 'accept')
      ? 'accepted'
      : affected.every((e) => choice[e.id] === 'keep')
        ? 'rejected'
        : null
    : null;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent
        className="sps-publish-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Publish v{plan.toVersion}</DialogTitle>
          <DialogDescription>
            {affected.length
              ? `${blocksLine} · Variants affected: ${names}`
              : `${blocksLine}. No variant is affected — the draft becomes the published version.`}
          </DialogDescription>
        </DialogHeader>

        {notice && (
          <p className="sps-pub-notice" role="alert">
            {notice}
          </p>
        )}

        {affected.length > 0 && (
          <div className="sps-pub-body">
            <p className="sps-pub-lead">
              Every variant answers before this publish lands. Accept takes the new master text;
              Keep mine keeps the variant&rsquo;s own words.
            </p>
            <div className="sps-pub-all">
              <span className="sps-pub-all-label" id="sps-pub-all">
                Apply to all variants
              </span>
              <div className="dsg-seg" role="group" aria-labelledby="sps-pub-all">
                <button
                  type="button"
                  className={`dsg-seg-btn${allChoice === 'accepted' ? ' is-active' : ''}`}
                  aria-pressed={allChoice === 'accepted'}
                  onClick={() => applyToAll('accepted')}
                  disabled={busy}
                >
                  Accept all
                </button>
                <button
                  type="button"
                  className={`dsg-seg-btn${allChoice === 'rejected' ? ' is-active' : ''}`}
                  aria-pressed={allChoice === 'rejected'}
                  onClick={() => applyToAll('rejected')}
                  disabled={busy}
                >
                  Keep mine
                </button>
              </div>
            </div>

            <ul className="sps-pub-rows">
              {affected.map((entry) => {
                const name = variantNameOf(plan.layers, entry);
                const k = pendingCount(entry);
                const row = choice[entry.id];
                const labelId = `sps-pub-row-${entry.id}`;
                return (
                  <li key={entry.id} className="sps-pub-row">
                    <div className="sps-pub-row-head">
                      <span className="sps-pub-row-name" id={labelId}>
                        {name}
                      </span>
                      <span className="sps-pub-row-count">
                        {k} change{k === 1 ? '' : 's'}
                      </span>
                      <div className="dsg-seg" role="group" aria-labelledby={labelId}>
                        {CHOICES.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className={`dsg-seg-btn${row === c.id ? ' is-active' : ''}`}
                            aria-pressed={row === c.id}
                            aria-expanded={c.id === 'review' ? row === 'review' : undefined}
                            onClick={() => setRow(entry, c.id)}
                            disabled={busy}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {row === 'review' && (
                      <div className="sps-pub-review">
                        <UpdatesPanel
                          clientName={name}
                          entries={[withAnswers(entry, answers)]}
                          canDecide={!busy}
                          busy={busy}
                          focusChangeId={null}
                          onDecide={(entryId, changeId, decision) =>
                            decideOne(entryId, changeId, decision)
                          }
                          onDecideAll={(_entryId, decision) => decideAllOf(entry, decision)}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onPublish(answers)} disabled={busy || !ready}>
            {busy ? 'Publishing…' : `Publish v${plan.toVersion}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
